import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from 'react-fetching-library';

import {
  createEmailCheckEvent,
  createFetchSelfClippingData,
  createFetchSubscriberData,
  createParticipantOnboarding,
  sendEmailForSubscripeParticipant,
} from 'api/actions/selfRegistration/selfRegistration.actions';
import { CreateParticipantBody, SelfClippingDataResponse } from 'api/actions/selfRegistration/selfRegistration.types';
import { SelfRegistrationPage } from './HighSchoolSelfRegistration';
import { HighSchoolSelfRegisterContainerProps } from './HighSchoolSelfRegistration.types';
import { useLocale } from 'shared/hooks';
import { participantSelection } from 'api/actions/participantSelection/participantClipping.actions';
import { ParticipantSelectionResponse } from 'api/actions/participantSelection/participantClipping.types';

export const HighSchoolSelfRegisterContainer = ({
  activeStep,
  setActiveStep,
  eventId,
  setParticipantDetails,
  isCollective,
  setSnackbarMessage,
  queryParamKeys,
  instituteLogo,
  isRetail,
  isAllowForAmazonMusic,
  institutionId,
}: HighSchoolSelfRegisterContainerProps) => {
  const { formatMessage } = useLocale();
  const [participant, setParticipant] = useState<any>();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [participantSelectionLoading, setParticipantSelectionLoading] = useState(false);
  const [linkedInEmail, setLinkedInEmail] = useState<string | null>(null);
  const [linkedInParticipantId, setLinkedInParticipantId] = useState<string | null>(null);

  const { mutate, loading } = useMutation(createParticipantOnboarding(eventId));
  const { mutate: CheckEmailExist } = useMutation(createEmailCheckEvent(eventId));

  const { mutate: sendEmail } = useMutation(sendEmailForSubscripeParticipant);
  const { payload: clipData } = useQuery<SelfClippingDataResponse>(createFetchSelfClippingData(eventId));
  const { payload: participantSelections, error: participantSelectionError, query: getSugessions } = useQuery<
    ParticipantSelectionResponse
  >(participantSelection(participant?.id), false);
  const { mutate: getParticipantDetails, loading: getParticipantDetailsLoading } = useMutation(
    createFetchSubscriberData,
  );

  const popupRef = useRef<Window | null>(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleNextSteps = useCallback(
    (email: any, participantSelectionsList?: any) => {
      const blockedDomains = ['yahoomail', 'yopmail'];
      const isBlockedDomain = blockedDomains.some(domain => email.includes(domain));

      if (clipData?.isSelfClipping && !participantSelectionError && participantSelectionsList) {
        const participantSelectionURL = `${process.env.REACT_APP_SELF_CLIPPING_URI ||
          window.location.origin}/participant-selection/${participant?.id}`;
        window.open(participantSelectionURL, '_blank');
      } else if (
        clipData?.isSelfClipping &&
        !participantSelectionsList &&
        isRetail &&
        participant?.wooCommercePermalink
      ) {
        setActiveStep(activeStep + 2);
        window.open(participant?.wooCommercePermalink, '_blank');
      } else if (isCollective && isBlockedDomain) {
        setActiveStep(activeStep + 2);
        setSnackbarMessage(
          <div>
            <div>One More Step!</div>
            <div>
              We need to verify you. Please complete this {email}{' '}
              <a href="https://stageclip.com/student-support/" target="_blank" rel="noopener noreferrer">
                form
              </a>
            </div>
            <div>{formatMessage({ id: 'highSchool.register_welcome' })}</div>
          </div>,
        );
      } else if ((isCollective && isRetail) || (isAllowForAmazonMusic && isRetail)) {
        setActiveStep(activeStep + 2);
      } else if (isAllowForAmazonMusic || isCollective) {
        setActiveStep(activeStep + 1);
      } else {
        setActiveStep(activeStep + 2);
      }
    },
    [
      activeStep,
      clipData,
      formatMessage,
      isAllowForAmazonMusic,
      isCollective,
      isRetail,
      participant,
      participantSelectionError,
      setActiveStep,
      setSnackbarMessage,
    ],
  );

  const handleLinkedNextSteps = useCallback(
    async (email: any, participantId: any, participantSuggestions?: any) => {
      const blockedDomains = ['yahoomail', 'yopmail'];
      const isBlockedDomain = blockedDomains.some(domain => email.includes(domain));

      if (clipData?.isSelfClipping && !participantSelectionError && participantSuggestions) {
        const participantSelectionURL = `${process.env.REACT_APP_SELF_CLIPPING_URI ||
          window.location.origin}/participant-selection/${participantId}`;
        window.open(participantSelectionURL, '_blank');
      } else if (
        clipData?.isSelfClipping &&
        !participantSuggestions &&
        isRetail &&
        !!participant?.wooCommercePermalink
      ) {
        setActiveStep(activeStep + 2);
        window.open(participant?.wooCommercePermalink, '_blank');
      } else if (isCollective && isBlockedDomain) {
        setActiveStep(activeStep + 2);
        setSnackbarMessage(
          <div>
            <div>One More Step!</div>
            <div>
              We need to verify you. Please complete this {email}{' '}
              <a href="https://stageclip.com/student-support/" target="_blank" rel="noopener noreferrer">
                form
              </a>
            </div>
            <div>{formatMessage({ id: 'highSchool.register_welcome' })}</div>
          </div>,
        );
      } else if ((isCollective && isRetail) || (isAllowForAmazonMusic && isRetail)) {
        setActiveStep(activeStep + 2);
      } else if (isAllowForAmazonMusic || isCollective) {
        setActiveStep(activeStep + 1);
      } else {
        setActiveStep(activeStep + 2);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      activeStep,
      clipData,
      formatMessage,
      isAllowForAmazonMusic,
      isCollective,
      isRetail,
      participantSelectionError,
      setActiveStep,
      setSnackbarMessage,
      participant,
    ],
  );

  const onSubmit = async (body: CreateParticipantBody) => {
    // eslint-disable-next-line no-param-reassign
    body.registerWithLinkedIn = false;
    const res = await mutate(body);
    setParticipantSelectionLoading(true);
    setParticipant(res.payload);

    if (res.error) {
      setSnackbarMessage(res.payload.errors.email.replace('"email"', `"${body?.email}"`));
      setParticipantSelectionLoading(false);

      return;
    }

    if (!isCollective && res.payload.id) {
      await sendEmail(res.payload.id);
    }

    if (res?.payload?.wooCommercePermalink) {
      setParticipantDetails({
        id: res?.payload?.id,
        wooCommercePermalink: res.payload.wooCommercePermalink,
      });
    }

    setIsSubmitted(true);
    // setParticipantSelectionLoading(true);

    if (!clipData?.isSelfClipping) {
      handleNextSteps(res.payload.email);
    }
  };

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.source === 'linkedin-auth' && event.data?.payload) {
        const { payload } = event.data;

        if (payload?.participantId?.email) {
          getParticipantDetails(payload?.linkedinData?.participantId).then(res => {
            setParticipant(res.payload);
            setLinkedInEmail(payload?.participantId?.email);
            setLinkedInParticipantId(payload?.linkedinData?.participantId);
          });
        }

        if (popupRef.current && !popupRef.current.closed) {
          popupRef.current.close();
          popupRef.current = null;
        }
      }
    };

    window.addEventListener('message', handleMessage);

    return () => window.removeEventListener('message', handleMessage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (linkedInEmail && linkedInParticipantId) {
      if (clipData?.isSelfClipping && isRetail) {
        getSugessions().then(res => {
          if (res.payload?.suggestions?.length) {
            handleLinkedNextSteps(linkedInEmail, linkedInParticipantId, res.payload?.suggestions);
          } else {
            handleLinkedNextSteps(linkedInEmail, linkedInParticipantId);
          }
        });
      } else {
        handleLinkedNextSteps(linkedInEmail, linkedInParticipantId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedInEmail, linkedInParticipantId]);

  const handleOnSubmitLinkedIn = async (body: CreateParticipantBody) => {
    const res = await CheckEmailExist({ email: body.email });

    if (res.error) {
      setSnackbarMessage(res.payload.errors.email.replace('"email"', `"${body.email}"`));
    } else {
      const linkedInURL = `https://www.linkedin.com/oauth/v2/authorization?client_id=${
        process.env.REACT_APP_LINKEDIN_CLIENT_ID
      }&client_secret=${process.env.REACT_APP_LINKEDIN_CLIENT_SECRET}&redirect_uri=${encodeURIComponent(
        `${process.env.REACT_APP_LINKEDIN_REDIRECT_URI}`,
      )}&response_type=code&scope=w_member_social,r_basicprofile`;

      const width = 600;
      const height = 600;
      const left = window.innerWidth / 2 - width / 2;
      const top = window.innerHeight / 2 - height / 2;

      popupRef.current = window.open(
        linkedInURL,
        'LinkedIn Login',
        `width=${width},height=${height},top=${top},left=${left}`,
      );

      const timer = setInterval(() => {
        try {
          if (popupRef.current?.location.href.includes(linkedInURL)) {
            const url = new URL(popupRef.current.location.href);
            const code = url.searchParams.get('code');

            if (code) {
              popupRef.current.close();
              clearInterval(timer);
            }
          }
        } catch (err) {
          // Ignore cross-origin
        }

        if (popupRef.current?.closed) {
          clearInterval(timer);
        }
      }, 1000);
      localStorage.setItem('LinkedInUser', JSON.stringify(body));
      localStorage.setItem('eventId', eventId);
    }
  };

  useEffect(() => {
    if (clipData?.isSelfClipping && isSubmitted && !participantSelections) {
      getSugessions().then(res => {
        const sugessionsLength = res.payload?.suggestions?.length ?? 0;

        if (res.payload) {
          setParticipantSelectionLoading(false);
        }

        if (res.payload?.suggestions && sugessionsLength > 0) {
          handleNextSteps(participant.email, res.payload?.suggestions);
        } else {
          handleNextSteps(participant.email);
        }
      });
    }
  }, [participant, isSubmitted, clipData, getSugessions, participantSelections, handleNextSteps]);

  return (
    <>
      <SelfRegistrationPage
        activeStep={activeStep}
        onSubmit={onSubmit}
        handleOnSubmitLinkedIn={handleOnSubmitLinkedIn}
        instituteLogo={instituteLogo}
        isCollective={isCollective}
        isLoading={
          loading || (!!clipData?.isSelfClipping && !!participantSelectionLoading) || getParticipantDetailsLoading
        }
        queryParamKeys={queryParamKeys}
        isRetail={isRetail}
        isAllowForAmazonMusic={isAllowForAmazonMusic}
        participantDetails={participant}
        isFromSelfOnBoard={clipData?.isFromSelfOnBoard}
        institutionId={institutionId}
      />
    </>
  );
};
